import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NavigationExtras, QueryParamsHandling } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';

@Component({
  selector: 'app-organizer-shared-form-wrapper',
  templateUrl: './form-wrapper.component.html',
})
export class OrganizerSharedFormWrapperComponent implements OnInit, OnDestroy {
  @Output() private readonly clickNext = new EventEmitter<void>();
  @Input() public activeStep = -1;
  @Input() public error$: Observable<boolean>;
  @Input() public formGroup: FormGroup;
  @Input() public hasPreviousButton = true;
  @Input() public loading$: Observable<boolean>;
  @Input() public nextNavigationTabIndex = 0;
  @Input() public previousNavigationExtras: NavigationExtras = {};
  @Input() public previousNavigationTabIndex = 0;
  @Input() public previousQueryParametersHandling: QueryParamsHandling =
    'preserve';
  @Input() public previousUrl = '';
  @Input() public sectionDescription = '';
  @Input() public sectionTitle = '';
  @Input() public tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] =
    [];
  @Input() public watchTipsChanges = false;
  @Output() public readonly formSubmit = new EventEmitter<unknown>();
  private errorSubscription: Subscription;
  private formGroupSubscription: Subscription;
  private isLoading = false;
  private loadingSubscription: Subscription;

  public hasMadeFailedSubmissionAttempt = false;
  public isNextDisabled = true;

  constructor(private readonly translateService: TranslateService) {}

  public ngOnDestroy(): void {
    this.unsubscribeError();
    this.unsubscribeLoading();
    this.unsubscribeFormGroupSubscription();
  }

  public ngOnInit(): void {
    this.handleError();
    this.handleLoading();
    this.handleFormGroup();
  }

  public get canSubmit(): boolean {
    return this.formGroup.valid && !this.isLoading;
  }

  private handleError(): void {
    this.errorSubscription = this.error$.subscribe(() => {
      // Handle error here
      this.isLoading = false;
    });
  }

  private handleFormGroup(): void {
    if (this.formGroup) {
      this.formGroupSubscription = this.formGroup.valueChanges.subscribe(() => {
        if (this.formGroup.valid) {
          this.hasMadeFailedSubmissionAttempt = false;
          this.isNextDisabled = false;
          return;
        }

        if (this.hasMadeFailedSubmissionAttempt) {
          this.isNextDisabled = true;
        }
      });

      const transArray = [
        this.translateService.instant(
          'activity_creation.duration.section.title'
        ),
        this.translateService.instant(
          'activity_creation.audience.section.title'
        ),
        this.translateService.instant(
          'activity_creation.equipment-provided.section.title'
        ),
        this.translateService.instant(
          'activity_creation.equipment-required.section.title'
        ),
        this.translateService.instant('activity_creation.plans.section.title'),
        this.translateService.instant(
          'organizer.activity_creation.birthday.upload.button'
        ),
        this.translateService.instant(
          'organizer.creation.birthday.sport_offer.title'
        ),
      ];

      if (transArray.indexOf(this.sectionTitle) !== -1) {
        this.isNextDisabled = false;
      }
    }
  }

  private handleLoading(): void {
    if (this.loading$) {
      this.loadingSubscription = this.loading$.subscribe(
        (isLoading: boolean) => {
          this.isLoading = isLoading;
        }
      );
    }
  }

  private unsubscribeError(): void {
    this.errorSubscription.unsubscribe();
  }

  private unsubscribeFormGroupSubscription(): void {
    if (this.formGroup && this.formGroupSubscription) {
      this.formGroupSubscription.unsubscribe();
    }
  }

  private unsubscribeLoading(): void {
    if (this.loading$) {
      this.loadingSubscription.unsubscribe();
    }
  }

  public clickNextNavigationButton(): void {
    this.clickNext.emit();
  }

  public submit(): void {
    if (!this.canSubmit) {
      this.isNextDisabled = true;
      this.hasMadeFailedSubmissionAttempt = true;
      this.formSubmit.emit(this.formGroup.value);
      return;
    }
    this.isNextDisabled = false;

    this.isLoading = true;

    this.formSubmit.emit(this.formGroup.value);
  }
}
